import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAccount, useChainId, useSwitchChain, useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import BettingPanel from './BettingPanel';
import ErrorModal from './ErrorModal';
import { isFixtureInUpcomingTab } from '../utils/fixtureFilters';
import logger from '../utils/logger';
import { getCompetitionLogoUrl } from '../utils/competitionLogos';
import { getPenaltyFromScore } from '../utils/fixtureScoreDisplay';
import { useWalletPortfolio } from '../context/WalletPortfolioContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://3dragons.io';

function getSportIcon(sport) {
  if (sport === 'rugby') return '🏉';
  return '⚽';
}

function FixtureCompetitionIcon({ fixture }) {
  const [imgFailed, setImgFailed] = useState(false);
  const logoUrl = getCompetitionLogoUrl(fixture);

  if (!logoUrl || imgFailed) {
    return <span style={{ fontSize: '18px' }}>{getSportIcon(fixture?.sport)}</span>;
  }

  return (
    <img
      src={logoUrl}
      alt={fixture.competition || 'Competition'}
      width="36"
      height="36"
      loading="lazy"
      decoding="async"
      style={{ height: '36px', objectFit: 'contain' }}
      title={fixture.competition}
      onError={() => setImgFailed(true)}
    />
  );
}

function formatDate(timestamp) {
  const date = new Date(timestamp * 1000);
  return date.toLocaleString('en-ZA', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatUsdc(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '0';
  return n.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
}

// Countdown component
function Countdown({ kickoffTime }) {
  const [timeLeft, setTimeLeft] = useState('');
  
  useEffect(() => {
    const updateCountdown = () => {
      const now = Math.floor(Date.now() / 1000);
      const diff = kickoffTime - now;
      
      if (diff <= 0) {
        setTimeLeft('Started');
        return;
      }
      
      const days = Math.floor(diff / 86400);
      const hours = Math.floor((diff % 86400) / 3600);
      const minutes = Math.floor((diff % 3600) / 60);
      const seconds = diff % 60;
      
      if (days > 0) {
        setTimeLeft(`${days}d ${hours}h`);
      } else if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m`);
      } else {
        setTimeLeft(`${minutes}m ${seconds}s`);
      }
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [kickoffTime]);
  
  return <span>{timeLeft}</span>;
}

// Payout calculator component (reserved for future use)
// eslint-disable-next-line no-unused-vars
function PayoutCalculator({ poolData }) {
  const [betAmount, setBetAmount] = useState('10');
  const [selectedOutcome, setSelectedOutcome] = useState('home');
  
  const calculatePayout = () => {
    if (!poolData || !betAmount || isNaN(betAmount)) return null;
    
    const amount = parseFloat(betAmount);
    if (amount <= 0) return null;
    
    const { homePool, drawPool, awayPool, totalPool } = poolData;
    const currentTotal = parseFloat(totalPool);
    const newTotal = currentTotal + amount;
    
    // Calculate new pool for selected outcome
    let outcomePool = 0;
    if (selectedOutcome === 'home') outcomePool = parseFloat(homePool);
    else if (selectedOutcome === 'draw') outcomePool = parseFloat(drawPool);
    else if (selectedOutcome === 'away') outcomePool = parseFloat(awayPool);
    
    const newOutcomePool = outcomePool + amount;
    
    // If you win, you get (totalPool * 98%) * (yourBet / outcomePool)
    const payout = (newTotal * 0.95) * (amount / newOutcomePool);
    const profit = payout - amount;
    
    return {
      payout: payout.toFixed(2),
      profit: profit.toFixed(2),
      roi: ((profit / amount) * 100).toFixed(1)
    };
  };
  
  const result = calculatePayout();
  
  return (
    <div style={{
      marginTop: '10px',
      padding: '12px',
      background: '#f8f9fa',
      borderRadius: '8px'
    }}>
      <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
        Payout Calculator
      </div>
      
      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
        <input
          type="number"
          value={betAmount}
          onChange={(e) => setBetAmount(e.target.value)}
          placeholder="Trade size"
          style={{
            flex: 1,
            padding: '6px',
            borderRadius: '4px',
            border: '1px solid #ddd',
            fontSize: '12px'
          }}
        />
        <select
          value={selectedOutcome}
          onChange={(e) => setSelectedOutcome(e.target.value)}
          style={{
            flex: 1,
            padding: '6px',
            borderRadius: '4px',
            border: '1px solid #ddd',
            fontSize: '12px'
          }}
        >
          <option value="home">Home</option>
          <option value="draw">Draw</option>
          <option value="away">Away</option>
        </select>
      </div>
      
      {result && (
        <div style={{ fontSize: '12px', color: '#666' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span>Potential Payout:</span>
            <strong style={{ color: '#28a745' }}>{result.payout} USDC</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span>Profit:</span>
            <strong style={{ color: result.profit >= 0 ? '#28a745' : '#dc3545' }}>
              {result.profit >= 0 ? '+' : ''}{result.profit} USDC
            </strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>ROI:</span>
            <strong style={{ color: result.profit >= 0 ? '#28a745' : '#dc3545' }}>
              {result.roi}%
            </strong>
          </div>
        </div>
      )}
      
      {!result && (
        <div style={{ fontSize: '12px', color: '#999', textAlign: 'center' }}>
          Enter trade size to see potential payout
        </div>
      )}
    </div>
  );
}

// Status badge component - only show for important statuses
function StatusBadge({ liveStatus }) {
  if (!liveStatus) return null;
  
  const { status, elapsed, score } = liveStatus;
  const hasScore = score && score.home !== null && score.away !== null;
  
  // Don't show badge for "Scheduled" (NS/TBD) - it's redundant on "Upcoming Fixtures" page
  if (status === 'NS' || status === 'TBD') {
    return null;
  }
  
  let badgeColor = '#6c757d'; // default gray
  let badgeText = '';
  
  // Format score if available (check that score exists and both values are not null)
  const scoreText = hasScore
    ? ` ${score.home}-${score.away}` 
    : '';
  
  if (status === '1H' || status === '2H' || status === 'HT' || status === 'ET' || status === 'P' || status === 'LIVE') {
    badgeColor = '#dc3545'; // red for live
    badgeText = `🔴 LIVE ${elapsed ? `(${elapsed}')` : ''}${scoreText}`;
  } else if (status === 'FT' || status === 'AET' || status === 'PEN') {
    badgeColor = '#28a745'; // green for finished
    badgeText = `✅ Finished${scoreText}`;
  } else if (status === 'PST' || status === 'CANC' || status === 'ABD') {
    if (hasScore) {
      badgeColor = '#28a745'; // If score exists, treat as completed
      badgeText = `✅ Finished${scoreText}`;
    } else {
      badgeColor = '#ffc107'; // yellow for delayed/cancelled
      badgeText = status === 'PST' ? '⏸️ Postponed' : '❌ Cancelled';
    }
  }
  
  // Only render if we have text to show
  if (!badgeText) return null;
  
  return (
    <span
      className="status-badge"
      style={{
        background: badgeColor,
        color: 'white',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: 'bold'
      }}
    >
      {badgeText}
    </span>
  );
}

/**
 * FixturesList component with enriched market data
 */
// Market ABI for claiming
const MARKET_ABI = [
  {
    name: 'claim',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [],
    outputs: []
  },
  {
    name: 'claimRefund',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [],
    outputs: []
  },
  {
    name: 'getUserBet',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: '_user', type: 'address' }, { name: '_outcome', type: 'uint8' }],
    outputs: [{ type: 'uint256' }]
  },
  {
    name: 'hasClaimed',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: '', type: 'address' }],
    outputs: [{ type: 'bool' }]
  },
  {
    name: 'hasClaimedRefund',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: '', type: 'address' }],
    outputs: [{ type: 'bool' }]
  },
  {
    name: 'state',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint8' }] // 0=OPEN, 1=CLOSED, 2=RESOLVED, 3=CANCELLED
  }
];

function FixturesList({ sport = 'all', leagueLabel = null, fixtureStatus = 'upcoming' }) {
  const navigate = useNavigate();
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsedRows, setCollapsedRows] = useState(new Set());
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '', type: 'error' });
  const { claimableByMarket: claimableFixtures, userMarketPositions, refreshPortfolio } =
    useWalletPortfolio();
  const [claimingMarket, setClaimingMarket] = useState(null);

  // Write contract for claiming
  const { writeContract: claimWinnings, data: claimHash, reset: resetClaim } = useWriteContract();

  // Wait for transaction
  const { isLoading: isClaiming, isSuccess: claimSuccess } = useWaitForTransactionReceipt({
    hash: claimHash,
  });

  const fetchFixtures = useCallback(async () => {
    try {
      setLoading(false); // Don't show loading on refresh
      setError(null);

      // Use enriched endpoint for full data
      // CRITICAL: Ensure sport parameter is properly encoded and passed
      const sportParam = sport || 'all';
      const cacheBust = Date.now(); // Prevent CDN/proxy from serving stale cached response
      const url = `${BACKEND_URL}/api/fixtures/enriched?sport=${encodeURIComponent(sportParam)}&_t=${cacheBust}`;
      logger.debug(`[FixturesList] Fetching fixtures for sport: ${sportParam}`);
      
      const response = await axios.get(url);
      let list = response.data.data || [];
      
      // Filter by league if sport is not 'all'
      if (sportParam !== 'all') {
        const filtered = list.filter(fixture => {
          const fixtureLeague = (fixture.league || '').toLowerCase();
          return fixtureLeague === sportParam.toLowerCase();
        });
        logger.debug(`[FixturesList] Filtered ${list.length} fixtures to ${filtered.length} for league: ${sportParam}`);
        list = filtered;
      }
      
      list.sort((a, b) => Number(a.kickoffTime) - Number(b.kickoffTime));
      setFixtures(list);
    } catch (err) {
      console.error('Error fetching fixtures:', err);
      setError(
        err.response?.data?.error || 
        'Failed to load fixtures. Make sure backend is running and API key is configured.'
      );
    } finally {
      setLoading(false);
    }
  }, [sport]);

  useEffect(() => {
    logger.debug(`[FixturesList] sport prop changed to: ${sport}`);
    fetchFixtures();
    // Refresh more frequently for live games (every 2 minutes) to show updated scores
    // For non-live games, 5 minutes is sufficient
    const interval = setInterval(fetchFixtures, 120000); // Refresh every 2 minutes for live score updates
    return () => clearInterval(interval);
  }, [fetchFixtures, sport]);

  // Handle claim success
  useEffect(() => {
    if (claimSuccess && claimHash) {
      // Transaction confirmed - refresh data
      
      // Immediately update claimable fixtures (optimistic update)
      setClaimingMarket(null);
      
      // Wait 4 seconds for blockchain to fully update
      setTimeout(async () => {
        await fetchFixtures();
        await refreshPortfolio();

      // Store the transaction hash before resetting
      const txHash = claimHash;
      
      // NOW show the success modal after data is refreshed
      setErrorModal({
        isOpen: true,
        title: 'Claim Successful! 🎉',
        message: `Your winnings have been claimed and your balance has been updated!\n\nTransaction: ${txHash.slice(0, 10)}...${txHash.slice(-8)}\n\nClick OK to view on Polygonscan.`,
        type: 'success',
        linkUrl: `https://polygonscan.com/tx/${txHash}`,
        linkLabel: 'View this transaction on PolygonScan'
      });
      
      // Reset the claim state to prevent this effect from running again
      resetClaim();
    }, 4000);
  }
}, [claimSuccess, claimHash, fetchFixtures, refreshPortfolio, resetClaim]);

  const handleClaimFixture = async (fixture) => {
    if (!isConnected) {
      setErrorModal({
        isOpen: true,
        title: 'Wallet Not Connected',
        message: 'Please connect your wallet to claim winnings or refunds',
        type: 'warning'
      });
      return;
    }

    if (chainId !== 137) {
      setErrorModal({
        isOpen: true,
        title: 'Wrong Network',
        message: 'Please switch your wallet to the Polygon network to claim.',
        type: 'warning'
      });
      try {
        switchChain?.({ chainId: 137 });
      } catch (_) {
        /* user can switch manually */
      }
      return;
    }

    if (!fixture.marketAddress) {
      setErrorModal({
        isOpen: true,
        title: 'No Market',
        message: 'This fixture does not have a market contract',
        type: 'error'
      });
      return;
    }

    try {
      setClaimingMarket(fixture.marketAddress);
      
      const marketState = fixture.marketState; // OPEN, CLOSED, RESOLVED, CANCELLED
      const hasScore =
        fixture.liveStatus?.score &&
        fixture.liveStatus.score.home !== null &&
        fixture.liveStatus.score.away !== null;
      const isApiCancelledWithoutScore = ['PST', 'CANC', 'ABD'].includes(fixture.status) && !hasScore;
      const isCancelled = marketState === 'CANCELLED' || isApiCancelledWithoutScore;
      
      // Determine if this is a refund (cancelled market) or winnings (resolved market)
      const isRefund = isCancelled;
      const functionName = isRefund ? 'claimRefund' : 'claim';
      claimWinnings({
        address: fixture.marketAddress,
        abi: MARKET_ABI,
        functionName: functionName,
      });
    } catch (error) {
      console.error('❌ Error claiming:', error);
      setErrorModal({
        isOpen: true,
        title: 'Claim Failed',
        message: error.message || 'Failed to submit claim. Please try again.',
        type: 'error'
      });
      setClaimingMarket(null);
    }
  };

  const handleFixtureClick = (fixture, e) => {
    // Don't navigate if clicking on buttons, betting interface, or other interactive elements
    if (e.target.closest('button') || 
        e.target.closest('.betting-panel') || 
        e.target.closest('input') || 
        e.target.closest('select') ||
        e.target.closest('.payout-calculator')) {
      return;
    }

    // Only navigate if fixture has a market
    if (fixture.hasMarket && fixture.marketAddress) {
      navigate(`/market/${fixture.marketAddress}`);
    }
  };

  const toggleRowCollapse = (rowIndex) => {
    setCollapsedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowIndex)) {
        newSet.delete(rowIndex);
      } else {
        newSet.add(rowIndex);
      }
      return newSet;
    });
  };

  // Filter fixtures based on status
  // CRITICAL: "Upcoming" = OPEN + CLOSED markets (including live games)
  //           "Finished" = RESOLVED markets only
  const filteredFixtures = useMemo(() => {
    let filtered;
    
    if (fixtureStatus === 'upcoming') {
      // Show OPEN and CLOSED markets (including live games)
      // EXCLUDE: Finished/resolved markets (FT, AET, PEN, RESOLVED state)
      filtered = fixtures.filter(isFixtureInUpcomingTab);
      
      // Sort upcoming fixtures ascending (earliest first)
      filtered.sort((a, b) => Number(a.kickoffTime) - Number(b.kickoffTime));
    } else {
      // Show RESOLVED markets only (status = 'FT', 'AET', 'PEN')
      filtered = fixtures.filter(fixture => {
        const status = fixture.status || 'NS';
        const marketState = fixture.marketState;
        
        // Include: Finished (FT, AET, PEN) - these are RESOLVED markets
        const isFinished = ['FT', 'AET', 'PEN'].includes(status);
        
        // Also check marketState to ensure RESOLVED markets are included
        const isResolvedMarket = marketState === 'RESOLVED';
        
        // Include: Postponed (PST), Cancelled (CANC, ABD) - these are CANCELLED markets
        const hasScore =
          fixture.liveStatus?.score &&
          fixture.liveStatus.score.home !== null &&
          fixture.liveStatus.score.away !== null;
        const isCancelled = ['PST', 'CANC', 'ABD'].includes(status) && !hasScore;
        
        return isFinished || isResolvedMarket || isCancelled;
      });
      
      // Sort finished fixtures descending (latest first - most recently resolved games first)
      filtered.sort((a, b) => Number(b.kickoffTime) - Number(a.kickoffTime));
    }
    
    return filtered;
  }, [fixtures, fixtureStatus]);

  // Group filtered fixtures into rows of 3
  const fixtureRows = [];
  for (let i = 0; i < filteredFixtures.length; i += 3) {
    fixtureRows.push(filteredFixtures.slice(i, i + 3));
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading fixtures...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        <strong>Error:</strong> {error}
        <br /><br />
        <button onClick={fetchFixtures} className="btn btn-primary">
          Retry
        </button>
      </div>
    );
  }

  if (filteredFixtures.length === 0) {
    const leagueOnly = sport && sport !== 'all';
    const compName = (leagueLabel && String(leagueLabel).trim()) || (leagueOnly ? 'this competition' : null);

    let title;
    let bodyPrimary;
    let bodySecondary = null;

    if (fixtureStatus === 'upcoming') {
      if (leagueOnly && compName) {
        title = `No markets for ${compName}`;
        bodyPrimary = 'There are no prediction markets listed for this competition yet.';
        bodySecondary =
          'New markets are created automatically on a rolling basis for fixtures with kickoffs in the next ~14 days (plus eligibility checks). If nothing falls in that window yet, the list stays empty.';
      } else {
        title = 'No markets listed';
        bodyPrimary = 'There are no markets to show for your current filters.';
        bodySecondary =
          'Try another competition from the menu, or check back soon — markets are typically added for fixtures within about the next 14 days.';
      }
    } else if (leagueOnly && compName) {
      title = `No finished markets for ${compName}`;
      bodyPrimary =
        'Nothing to show for this competition yet. Finished and resolved markets from roughly the last 14 days appear here once they exist on-chain.';
    } else {
      title = 'No finished fixtures';
      bodyPrimary =
        'No finished or resolved markets match your filters. Try another competition or check back later.';
    }

    return (
      <div className="empty-state">
        <h2>{title}</h2>
        <p>{bodyPrimary}</p>
        {bodySecondary && (
          <p style={{ fontSize: '14px', marginTop: '18px', opacity: 0.85 }}>
            {bodySecondary}
          </p>
        )}
      </div>
    );
  }

  return (
    <div>
      {fixtureRows.map((row, rowIndex) => {
        const isCollapsed = collapsedRows.has(rowIndex);
        const rowStartNum = rowIndex * 3 + 1;
        const rowEndNum = Math.min(rowStartNum + row.length - 1, fixtures.length);
        
        return (
          <div key={rowIndex} style={{ marginBottom: '20px', position: 'relative' }}>
            {/* Collapsed State: Show summary bar */}
            {isCollapsed && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                cursor: 'pointer'
              }}
              onClick={() => toggleRowCollapse(rowIndex)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    style={{
                      background: 'none',
                      color: '#000',
                      border: 'none',
                      padding: 0,
                      fontSize: '20px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      lineHeight: 1
                    }}
                  >
                    +
                  </button>
                  <span style={{ color: '#333', fontSize: '13px', fontWeight: '600' }}>
                    Fixtures {rowStartNum}-{rowEndNum} ({row.length} {row.length === 1 ? 'match' : 'matches'})
                  </span>
                </div>
                
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {row.slice(0, 3).map((fixture, idx) => {
                    const rowLogo = getCompetitionLogoUrl(fixture);
                    return (
                    <div key={idx} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '4px',
                      fontSize: '11px',
                      color: '#666',
                      padding: '4px 8px',
                      background: 'white',
                      borderRadius: '4px',
                      border: '1px solid #e0e0e0'
                    }}>
                      {(rowLogo) && (
                        <img 
                          src={rowLogo} 
                          alt="" 
                          width="14"
                          height="14"
                          loading="lazy"
                          decoding="async"
                          style={{ height: '14px', objectFit: 'contain' }} 
                        />
                      )}
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px' }}>
                        {fixture.homeTeam} vs {fixture.awayTeam}
                      </span>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Expanded State: Show collapse button overlay + fixtures grid */}
            {!isCollapsed && (
              <>
                {/* Floating collapse button on top left of grid */}
                <button
                  onClick={() => toggleRowCollapse(rowIndex)}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    left: '8px',
                    background: 'transparent',
                    color: '#000',
                    border: 'none',
                    padding: '4px 8px',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    lineHeight: 1
                  }}
                >
                  -
                </button>
                
                <div className="markets-grid">
                {row.map((fixture) => {
                  const marketKey = fixture.marketAddress?.toLowerCase();
                  const userPosition = marketKey ? userMarketPositions[marketKey] : null;
                  const outcomeTotals = userPosition?.outcomeTotals || {};
                  const topOutcomeEntry = Object.entries(outcomeTotals).reduce(
                    (best, [outcome, amount]) => {
                      if (!best || amount > best.amount) {
                        return { outcome, amount };
                      }
                      return best;
                    },
                    null
                  );
                  const selectedOutcome = topOutcomeEntry?.amount > 0 ? topOutcomeEntry.outcome : 'MIXED';
                  const selectedStake = Number(topOutcomeEntry?.amount || 0);
                  const poolData = fixture.poolData || {};
                  const homePool = parseFloat(poolData.homePool || '0');
                  const drawPool = parseFloat(poolData.drawPool || '0');
                  const awayPool = parseFloat(poolData.awayPool || '0');
                  const totalPool = parseFloat(poolData.totalPool || '0');
                  const selectedPool = selectedOutcome === 'HOME'
                    ? homePool
                    : selectedOutcome === 'AWAY'
                      ? awayPool
                      : selectedOutcome === 'DRAW'
                        ? drawPool
                        : 0;
                  const impliedOdds = (totalPool > 0 && selectedPool > 0)
                    ? ((totalPool * 0.95) / selectedPool).toFixed(2)
                    : null;
                  const showScoreBlock = (fixture.status === 'FT' && fixture.score && fixture.score.home !== null && fixture.score.away !== null) ||
                    (fixture.marketState === 'CLOSED' && fixture.liveStatus?.score &&
                     fixture.liveStatus.score.home !== null && fixture.liveStatus.score.away !== null &&
                     fixture.liveStatus.status !== 'NS' && fixture.liveStatus.status !== 'TBD');
                  const displayScore = showScoreBlock
                    ? (fixture.status === 'FT' ? fixture.score : fixture.liveStatus.score)
                    : null;
                  const penalties = getPenaltyFromScore(displayScore);
                  const homeWonPens = penalties && fixture.marketResultId === 0;
                  const awayWonPens = penalties && fixture.marketResultId === 2;
                  return (
                  <div 
                    key={fixture.fixtureId} 
                    className="market-card"
                    onClick={(e) => handleFixtureClick(fixture, e)}
                    style={{ 
                      cursor: fixture.hasMarket && fixture.marketAddress ? 'pointer' : 'default',
                      position: 'relative'
                    }}
                  >
                    <div className="market-header fixture-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div className="team-names fixture-team-names" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}>
                        <FixtureCompetitionIcon fixture={fixture} />
                        <span className="fixture-team">
                        {fixture.homeTeamLogo && (
                          <img 
                            src={fixture.homeTeamLogo} 
                            alt={fixture.homeTeam} 
                            width="25"
                            height="25"
                            loading="lazy"
                            decoding="async"
                            style={{ width: '25px', height: '25px', objectFit: 'contain' }} 
                          />
                        )}
                        <span>{fixture.homeTeam}</span>
                        </span>
                        <span className="fixture-vs">vs</span>
                        <span className="fixture-team">
                        {fixture.awayTeamLogo && (
                          <img 
                            src={fixture.awayTeamLogo} 
                            alt={fixture.awayTeam} 
                            width="25"
                            height="25"
                            loading="lazy"
                            decoding="async"
                            style={{ width: '25px', height: '25px', objectFit: 'contain' }} 
                          />
                        )}
                        <span>{fixture.awayTeam}</span>
                        </span>
                      </div>
                    </div>

                    {isConnected && userPosition && (
                      <div style={{
                        marginTop: '8px',
                        marginBottom: '6px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 10px',
                        background: 'linear-gradient(135deg, rgba(100, 255, 218, 0.16) 0%, rgba(79, 214, 184, 0.2) 100%)',
                        border: '1px solid rgba(100, 255, 218, 0.6)',
                        borderRadius: '999px',
                        color: '#0a0e27',
                        fontSize: '11px',
                        fontWeight: '700'
                      }}>
                        <span>
                          You are in this market @ {formatUsdc(selectedStake || userPosition.totalSize)} USDC for {selectedOutcome}
                          {impliedOdds ? ` for ${impliedOdds}x` : ''}
                        </span>
                      </div>
                    )}

                    <div style={{ padding: '0' }}>

              {/* Show Score for Finished Games or Live Games (NOT for NS/TBD status) */}
              {showScoreBlock && displayScore && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '16px 0',
                  borderBottom: '1px solid #f0f0f0',
                  background: fixture.status === 'FT' ? '#f8f9fa' : '#fff3cd',
                  marginBottom: '10px',
                  borderRadius: '6px'
                }}>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#333', letterSpacing: '4px' }}>
                    {displayScore.home} - {displayScore.away}
                  </div>
                  {penalties && (
                    <div style={{ fontSize: '14px', color: '#666', marginTop: '6px' }}>
                      Penalties:{' '}
                      <span style={{ fontWeight: homeWonPens ? 700 : 400 }}>{penalties.home}</span>
                      {' - '}
                      <span style={{ fontWeight: awayWonPens ? 700 : 400 }}>{penalties.away}</span>
                    </div>
                  )}
                  {fixture.liveStatus?.elapsed && fixture.status !== 'FT' && (
                    <div style={{ fontSize: '14px', color: '#dc3545', marginTop: '4px', fontWeight: 'bold' }}>
                      🔴 LIVE ({fixture.liveStatus.elapsed}')
                    </div>
                  )}
                </div>
              )}

              {/* Match Info Box */}
              <div style={{
                marginTop: fixture.status === 'FT' && fixture.score ? '0' : '10px',
                padding: '12px',
                background: '#fff',
                border: '2px solid #e0e0e0',
                borderRadius: '8px'
              }}>
                {/* Top row: Kickoff and Status/Countdown side by side */}
                <div className="fixture-meta-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>
                      {fixture.status === 'FT' ? 'Match Date' : 'Kickoff'}
                    </div>
                    <div style={{ fontSize: '12px', fontWeight: 'bold' }}>
                      {formatDate(fixture.kickoffTime)}
                    </div>
                  </div>

                  {/* Show status badge for live/finished, or countdown/closed status for upcoming */}
                  {fixture.marketState === 'CANCELLED' ? (
                    <div style={{ flex: 1, textAlign: 'right' }}>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>Market</div>
                      <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#dc3545' }}>
                        Cancelled · refunds
                      </div>
                    </div>
                  ) : fixture.marketState === 'RESOLVED' ? (
                    <div style={{ flex: 1, textAlign: 'right' }}>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>Market</div>
                      <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#28a745' }}>Resolved</div>
                    </div>
                  ) : ['FT', 'AET', 'PEN', '1H', '2H', 'HT', 'ET', 'P', 'LIVE'].includes(fixture.liveStatus?.status) ? (
                    <div style={{ flex: 1, textAlign: 'right' }}>
                      <StatusBadge liveStatus={fixture.liveStatus} />
                    </div>
                  ) : (() => {
                    // Check if market is CLOSED and kickoff has passed
                    const now = Math.floor(Date.now() / 1000);
                    const kickoffPassed = fixture.kickoffTime && now >= fixture.kickoffTime;
                    const isMarketClosed = fixture.marketState === 'CLOSED';
                    
                    if (isMarketClosed && kickoffPassed) {
                      // Market is closed and game has started - show "Game started: Market closed"
                      return (
                        <div style={{ flex: 1, textAlign: 'right' }}>
                          <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>
                            Game started
                          </div>
                          <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#dc3545' }}>
                            Market closed
                          </div>
                          {/* Show live score if available */}
                          {fixture.liveStatus?.score && fixture.liveStatus.score.home !== null && fixture.liveStatus.score.away !== null && (
                            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#dc3545', marginTop: '4px' }}>
                              🔴 {fixture.liveStatus.score.home} - {fixture.liveStatus.score.away}
                              {fixture.liveStatus.elapsed && ` (${fixture.liveStatus.elapsed}')`}
                            </div>
                          )}
                        </div>
                      );
                    } else {
                      // Show countdown for upcoming markets
                      return (
                        <div style={{ flex: 1, textAlign: 'right' }}>
                          <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>
                            Closes in
                          </div>
                          <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#dc3545' }}>
                            <Countdown kickoffTime={fixture.kickoffTime} />
                          </div>
                        </div>
                      );
                    }
                  })()}
                </div>

                {/* Bottom row: Venue + Polyscan link */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>
                      Venue
                    </div>
                    <div style={{ fontSize: '12px', fontWeight: 'bold' }}>
                      {fixture.venue || 'TBD'}
                    </div>
                  </div>
                  {fixture.hasMarket && fixture.marketAddress && (
                    <a 
                      href={`https://polygonscan.com/address/${fixture.marketAddress}#tokentxns`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ 
                        fontSize: '11px',
                        color: '#6366f1',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3px',
                        opacity: 0.8,
                        transition: 'opacity 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.opacity = '1'}
                      onMouseLeave={(e) => e.target.style.opacity = '0.8'}
                      title="View contract on PolygonScan"
                    >
                      <span>View Contract on Polyscan</span>
                    </a>
                  )}
                </div>
              </div>

            </div>

            {/* Finished tab: chain-cancelled vs API FT vs claim UI */}
            {fixture.marketState === 'CANCELLED' && fixture.hasMarket ? (
              <div style={{
                marginTop: '10px',
                padding: '16px',
                background: '#6c757d',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ color: 'white', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
                  Market voided on-chain
                </div>
                <div style={{ color: 'white', fontSize: '12px', opacity: 0.95 }}>
                  Fixture may show a result from the feed, but this market was cancelled — refunds only.
                </div>
              </div>
            ) : fixture.status === 'FT' && fixture.hasMarket && fixture.poolData ? (
              <div style={{ 
                marginTop: '10px',
                padding: '16px',
                background: '#28a745',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ color: 'white', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
                  🏆 Match Completed
                </div>
                
                {/* Show payout pool if available */}
                {fixture.poolData && fixture.poolData.totalPool && (
                  <div style={{ color: 'white', fontSize: '12px', marginBottom: '8px' }}>
                    Total Payout Pool: {parseFloat(fixture.poolData.totalPool).toFixed(2)} USDC
                  </div>
                )}
                
                {/* Show ROI for winners if user has winnings */}
                {isConnected && claimableFixtures[fixture.marketAddress?.toLowerCase()] && fixture.poolData && (() => {
                  const claimable = claimableFixtures[fixture.marketAddress?.toLowerCase()];
                  // userBet arrives as a string ('0' fallback) from buildClaimableByMarket — coerce
                  // to a number before .toFixed()/arithmetic (was throwing "r.toFixed is not a function").
                  const userBet = parseFloat(claimable.userBet || 0);
                  const winnings = parseFloat(claimable.amount || 0);
                  const profit = winnings - userBet;
                  const roi = userBet > 0 ? ((profit / userBet) * 100).toFixed(1) : '0.0';
                  
                  return (
                    <div style={{ color: 'white', fontSize: '11px', marginBottom: '12px', opacity: 0.9 }}>
                      <div>Your Bet: {userBet.toFixed(2)} USDC</div>
                      <div>Winnings: {winnings.toFixed(2)} USDC</div>
                      <div style={{ fontWeight: 'bold', marginTop: '4px' }}>
                        ROI: {roi}% {profit >= 0 ? '✅' : '❌'}
                      </div>
                    </div>
                  );
                })()}
                
                {/* Show claim button only if user has winnings */}
                {isConnected && claimableFixtures[fixture.marketAddress?.toLowerCase()] ? (
                  <button 
                    style={{
                      background: (isClaiming && claimingMarket === fixture.marketAddress) ? 'rgba(255,255,255,0.5)' : 'white',
                      color: '#28a745',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: (isClaiming && claimingMarket === fixture.marketAddress) ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClaimFixture(fixture);
                    }}
                    disabled={isClaiming && claimingMarket === fixture.marketAddress}
                  >
                    {(isClaiming && claimingMarket === fixture.marketAddress) 
                      ? '⏳ Claiming...' 
                      : `💰 Claim ${parseFloat(claimableFixtures[fixture.marketAddress?.toLowerCase()].amount || 0).toFixed(2)} USDC`
                    }
                  </button>
                ) : isConnected ? (
                  <div style={{ color: 'white', fontSize: '12px', opacity: 0.8 }}>
                    ✓ No winnings to claim (or already claimed)
                  </div>
                ) : (
                  <div style={{ color: 'white', fontSize: '12px', opacity: 0.8 }}>
                    🔗 Connect wallet to check if you have winnings
                  </div>
                )}
              </div>
            ) : fixture.status === 'FT' ? (
              <div style={{ 
                marginTop: '10px',
                padding: '12px',
                background: '#e8f5e9',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <p style={{ margin: 0, color: '#2e7d32', fontSize: '13px' }}>
                  ✅ Match Finished
                </p>
                <p style={{ margin: '4px 0 0 0', color: '#2e7d32', fontSize: '11px' }}>
                  No market was created for this fixture
                </p>
              </div>
            ) : fixture.hasMarket ? (
              <div className="betting-panel">
                <BettingPanel 
                  fixture={fixture} 
                  poolData={fixture.poolData}
                  onTradeSuccess={() => {
                    fetchFixtures();
                    refreshPortfolio();
                  }}
                />
              </div>
            ) : (
            <div style={{ 
                marginTop: '10px',
                padding: '12px',
                background: '#fff3cd',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
                <p style={{ margin: 0, color: '#856404', fontSize: '13px' }}>
                ⚠️ Market not yet created
              </p>
                <p style={{ margin: '4px 0 0 0', color: '#856404', fontSize: '11px' }}>
                  Markets are created automatically. Check back soon.
              </p>
            </div>
            )}
          </div>
                  );
                })}
              </div>
              </>
            )}
          </div>
        );
      })}

      <div style={{
        marginTop: '24px',
        padding: '16px',
        background: 'rgba(255,255,255,0.9)',
        borderRadius: '10px',
        textAlign: 'center'
      }}>
        <p style={{ color: '#666', margin: 0 }}>
          Showing {fixtures.filter(f => f.status === 'FT').length} finished + {fixtures.filter(f => f.status !== 'FT').length} upcoming fixtures
        </p>
      </div>

      {/* Error Modal */}
      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ ...errorModal, isOpen: false })}
        title={errorModal.title}
        message={errorModal.message}
        type={errorModal.type}
      />
    </div>
  );
}

export default FixturesList;
